import React, { useState } from 'react';
import { X, Check } from 'lucide-react';
import { RyanDisplayCategoryData } from '../types/ryan.types';

interface SubdeckData {
  _id: string;
  name: string;
  type: 'flashcards' | 'apostila';
  displayCategoryId?: string | null;
}

interface RyanAssignCategoryModalProps {
  spaceId: string;
  subdeck: SubdeckData;
  categories: RyanDisplayCategoryData[];
  onClose: () => void;
  onSuccess: () => void;
  onToast: (msg: string, type: 'success' | 'error' | 'info') => void;
}

const RyanAssignCategoryModal: React.FC<RyanAssignCategoryModalProps> = ({
  spaceId, subdeck, categories, onClose, onSuccess, onToast,
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(subdeck.displayCategoryId || null);
  const [saving, setSaving] = useState(false);

  const defaultCategory = categories.find(c => c.isDefault && c.defaultForType === subdeck.type);
  const sortedCategories = [...categories].sort((a, b) => a.order - b.order);

  const handleSave = async () => {
    setSaving(true);
    const token = localStorage.getItem('accessToken');
    try {
      const res = await fetch(`/api/ryan/admin/spaces/${spaceId}/subdecks/${subdeck._id}/display-category`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ displayCategoryId: selectedCategoryId }),
      });
      if (!res.ok) {
        onToast('Erro ao atribuir categoria', 'error');
        return;
      }
      onToast('Categoria atribuída!', 'success');
      onSuccess();
      onClose();
    } catch {
      onToast('Erro de conexão', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ryan-modal-overlay" onClick={onClose}>
      <div className="ryan-modal" onClick={e => e.stopPropagation()}>
        <div className="ryan-modal-header">
          <h3 className="ryan-modal-title">Categoria de Exibição</h3>
          <button className="ryan-modal-close-btn" onClick={onClose}><X size={18} /></button>
        </div>

        <div className="ryan-modal-body">
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif', marginBottom: 14 }}>
            Deck: <strong style={{ color: '#0D3B0E' }}>{subdeck.name}</strong>
          </p>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {/* Opção: categoria padrão automática (não força nenhum displayCategoryId) */}
            <button
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                padding: '10px 14px',
                borderRadius: 10,
                border: `2px solid ${selectedCategoryId === null ? '#0D3B0E' : 'rgba(0,0,0,0.08)'}`,
                background: selectedCategoryId === null ? 'rgba(13,59,14,0.05)' : '#f8faf8',
                cursor: 'pointer',
                textAlign: 'left',
                transition: 'all 150ms',
              }}
              onClick={() => setSelectedCategoryId(null)}
            >
              <div style={{
                width: 32, height: 32, borderRadius: 8,
                background: defaultCategory ? `color-mix(in oklch, ${defaultCategory.color} 18%, transparent)` : '#f0f0f0',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: defaultCategory?.color || '#9CA3AF' }} />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#111', fontFamily: 'Inter, sans-serif' }}>
                  Automática {defaultCategory ? `(${defaultCategory.name})` : ''}
                </div>
                <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>
                  Segue o tipo do deck ({subdeck.type === 'apostila' ? 'Apostila' : 'Flashcards'})
                </div>
              </div>
              {selectedCategoryId === null && (
                <Check size={16} color="#0D3B0E" style={{ marginLeft: 'auto', flexShrink: 0 }} />
              )}
            </button>

            {sortedCategories.map(category => {
              const isSelected = selectedCategoryId === category._id;
              return (
                <button
                  key={category._id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: `2px solid ${isSelected ? category.color : 'rgba(0,0,0,0.08)'}`,
                    background: isSelected ? `color-mix(in oklch, ${category.color} 5%, transparent)` : '#f8faf8',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 150ms',
                  }}
                  onClick={() => setSelectedCategoryId(category._id)}
                >
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: `color-mix(in oklch, ${category.color} 18%, transparent)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: category.color }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: '#111', fontFamily: 'Inter, sans-serif' }}>
                      {category.name}
                    </div>
                    {category.isDefault && (
                      <div style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontFamily: 'Inter, sans-serif' }}>
                        Categoria padrão
                      </div>
                    )}
                  </div>
                  {isSelected && (
                    <Check size={16} color={category.color} style={{ flexShrink: 0 }} />
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="ryan-modal-footer">
          <button className="ryan-modal-cancel-btn" onClick={onClose}>Cancelar</button>
          <button className="ryan-modal-save-btn" onClick={handleSave} disabled={saving}>
            {saving ? 'Salvando…' : 'Confirmar'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RyanAssignCategoryModal;
